import {Box, Typography} from '@mui/material';
import {GridColDef} from '@mui/x-data-grid-pro';
import FilterTextField from '../../../../common/arr-data-grid-pro/filter-components/text-field/FilterTextField';
import {t} from 'i18next';
import './IssueCodeDataGrid.scss';
import {ReactComponent as AIIconRelevancy} from '../../../../../assets/images/ai-review-relevancy.svg';
import {ReactNode} from 'react';
import CellWrapper from '../../../../common/arr-data-grid-pro/filter-components/cell-wrapper/CellWrapper';
import ArrTooltip from '../../../../common/arr-tooltip/ArrTooltip';

const RELEVANT = 'RELEVANT';

const wrap = (content: ReactNode) => {
  const cell = (
    <CellWrapper className="tooltip_content" dataTestId="tooltip_content">
      {content}
    </CellWrapper>
  );

  return (
    <ArrTooltip
      trigger="hover"
      placement="bottom"
      content={content}
      labelId={`tooltip-cell-cont-${content}`}
    >
      {cell}
    </ArrTooltip>
  );
};
export const relevancyColumns = (): GridColDef[] => [
  {
    field: 'documentId',
    headerName: t('ai.review.jobTypeDataGrid.relevenacyColumns.docId'),
    sortable: true,
    filterable: false,
    minWidth: 129,
    flex: 1,
    renderHeader: () => (
      <Typography
        tabIndex={0}
        className="typo-text label2_size"
        data-testid={`relevancy-column-doc-id-header`}
      >
        {t('ai.review.jobTypeDataGrid.relevenacyColumns.docId')}
      </Typography>
    ),
    renderCell: (params) => (
      <Box
        tabIndex={0}
        aria-label={`${params.value}, ${t('ai.review.jobTypeDataGrid.relevenacyColumns.docId')}`}
        data-testid={`docId-column-${params.id}-box`}
      >
        <Typography
          className="cell-content typo-text subhead5_size job-value"
          data-testid={`relevancy-column-docId-${params.id}`}
          aria-hidden="true"
        >
          {params.value}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'relevancy',
    headerName: t('ai.review.jobTypeDataGrid.relevenacyColumns.relevancy'),
    sortable: false,
    filterable: true,
    minWidth: 170,
    flex: 1,
    renderCell: (params) => (
      <Box
        tabIndex={0}
        role="gridcell"
        data-testid={`relevancy-column-relevancy--${params.id}-box`}
      >
        <Typography
          className={`cell-content typo-text body3_size ${params.value !== RELEVANT && 'no_typo_text'}`}
          data-testid={`relevancy-column-relevancy--${params.id}`}
          tabIndex={0}
        >
          {params.value === RELEVANT ? 'Yes' : 'No'}
        </Typography>
      </Box>
    ),
    renderHeader: () => {
      return (
        <Box
          sx={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}
          tabIndex={0}
          role="columnheader"
          data-testid={`relevancy-column-relevancy-box`}
        >
          <AIIconRelevancy />
          <Typography
            className="typo-text label2_size"
            data-testid={`relevancy-column-relevancy-header`}
          >
            {t('ai.review.jobTypeDataGrid.relevenacyColumns.relevancy')}
          </Typography>
        </Box>
      );
    },
    renderHeaderFilter: (params) => (
      <FilterTextField
        dataTestId={`relevancy`}
        gridRenderHeaderFilterProps={params}
        placeholder={t('common.filter')}
      />
    ),
  },
  {
    field: 'groundTruthRelevancy',
    headerName: t('ai.review.jobTypeDataGrid.relevenacyColumns.groundTruth'),
    sortable: false,
    filterable: true,
    minWidth: 170,
    flex: 1,
    renderCell: (params) => (
      <Box
        tabIndex={0}
        role="gridcell"
        data-testid={`relevancy-column-groundTruth-${params.id}-box`}
      >
        <Typography
          className={`cell-content typo-text body3_size ${params.value !== RELEVANT && 'no_typo_text'}`}
          data-testid={`relevancy-column-groundTruth-${params.id}`}
          tabIndex={0}
        >
          {params.value === RELEVANT ? 'Yes' : 'No'}
        </Typography>
      </Box>
    ),
    renderHeader: () => (
      <Typography
        tabIndex={0}
        role="columnheader"
        className="typo-text label2_size"
        data-testid={`relevancy-column-ground-truth-headr`}
      >
        {t('ai.review.jobTypeDataGrid.relevenacyColumns.groundTruth')}
      </Typography>
    ),
    renderHeaderFilter: (params) => (
      <FilterTextField
        dataTestId={`groundTruth`}
        gridRenderHeaderFilterProps={params}
        placeholder={t('common.filter')}
      />
    ),
  },
  {
    field: 'reason',
    headerName: t('ai.review.jobTypeDataGrid.relevenacyColumns.reason'),
    sortable: false,
    filterable: true,
    minWidth: 393.5,
    flex: 1,
    renderHeader: () => (
      <Typography
        tabIndex={0}
        className="typo-text label2_size"
        role="columnheader"
        data-testid={`relevancy-column-reason-header`}
      >
        {t('ai.review.jobTypeDataGrid.relevenacyColumns.reason')}
      </Typography>
    ),
    renderCell: (params) => (
      <Box
        tabIndex={0}
        data-testid={`relevancy-column-excerpt-box-${params.id}`}
      >
        <Typography
          className="cell-content typo-text body3_size"
          data-testid={`relevancy-column-reason-${params.id}`}
        >
          {wrap(params.value)}
        </Typography>
      </Box>
    ),
    renderHeaderFilter: (params) => (
      <FilterTextField
        dataTestId={`reason`}
        gridRenderHeaderFilterProps={params}
        placeholder={t('common.filter')}
      />
    ),
  },
  {
    field: 'excerpt',
    headerName: t('ai.review.jobTypeDataGrid.relevenacyColumns.excerpt'),
    sortable: false,
    filterable: true,
    minWidth: 393.5,
    headerClassName: ' last-column',
    flex: 1,
    renderHeader: () => (
      <Typography
        tabIndex={0}
        className="typo-text label2_size"
        role="columnheader"
        data-testid={`relevancy-column-excerpt-header`}
      >
        {t('ai.review.jobTypeDataGrid.relevenacyColumns.excerpt')}
      </Typography>
    ),
    renderCell: (params) => (
      <Box
        tabIndex={0}
        data-testid={`relevancy-column-excerpt-box-${params.id}`}
      >
        <Typography
          className="cell-content typo-text body3_size typo_excerpt"
          data-testid={`relevancy-column-excerpt-${params.id}`}
        >
          {wrap(params.value)}
        </Typography>
      </Box>
    ),
    renderHeaderFilter: (params) => (
      <FilterTextField
        dataTestId={`excerpt`}
        gridRenderHeaderFilterProps={params}
        placeholder={t('common.filter')}
      />
    ),
  },
];
